import { AbstractControl, FormControl, FormGroup } from "@angular/forms";

import _merge from "lodash/merge";
import { Observable } from "rxjs";
import { merge } from "rxjs/internal/observable/merge";
import { map } from "rxjs/operators";

import { BaseObservableForm } from "../../../../shared-library/forms/base-observable.form";

import { SearchParametersModel } from "../models/search-parameters.model";

export class DirectorySearchForm extends BaseObservableForm<SearchParametersModel> {
    public static createForm(
        initialSearchParams: SearchParametersModel,
        emptySearchParams: SearchParametersModel
    ): DirectorySearchForm {
        const formGroup = new FormGroup({
            searchTab: new FormControl(initialSearchParams.searchTab),
            templateId: new FormControl(initialSearchParams.templateId),

            searchText: new FormControl(initialSearchParams.searchText),
            sortOption: new FormControl(initialSearchParams.sortOption),
            pageNumber: new FormControl(initialSearchParams.pageNumber),
            pageSize: new FormControl(initialSearchParams.pageSize),

            startDate: new FormControl(initialSearchParams.startDate),
            endDate: new FormControl(initialSearchParams.endDate),

            categories: new FormControl(initialSearchParams.categories),
            eventCategories: new FormControl(initialSearchParams.eventCategories),
            needsOfChildren: new FormControl(initialSearchParams.needsOfChildren),
            postcodes: new FormControl(initialSearchParams.postcodes),
            age: new FormControl(initialSearchParams.age),
            frequency: new FormControl(initialSearchParams.frequency),
            dietaryNeeds: new FormControl(initialSearchParams.dietaryNeeds),
            accessibility: new FormControl(initialSearchParams.accessibility),
            provisionTypes: new FormControl(initialSearchParams.provisionTypes),
            schoolNeeds: new FormControl(initialSearchParams.schoolNeeds),
            specialDietCater: new FormControl(initialSearchParams.specialDietCater),
            postcode: new FormControl(initialSearchParams.postcode),
            distance: new FormControl(initialSearchParams.distance)
        });

        return new DirectorySearchForm(formGroup, initialSearchParams, emptySearchParams);
    }

    public get instance(): FormGroup { return this.formInstance; }

    public get searchText(): AbstractControl { return this.getFormControl("searchText"); }
    public get sortOption(): AbstractControl { return this.getFormControl("sortOption"); }
    public get pageNumber(): AbstractControl { return this.getFormControl("pageNumber"); }
    public get pageSize(): AbstractControl { return this.getFormControl("pageSize"); }

    public get startDate(): AbstractControl { return this.getFormControl("startDate"); }
    public get endDate(): AbstractControl { return this.getFormControl("endDate"); }

    public get categories(): AbstractControl { return this.getFormControl("categories"); }
    public get eventCategories(): AbstractControl { return this.getFormControl("eventCategories"); }
    public get needsOfChildren(): AbstractControl { return this.getFormControl("needsOfChildren"); }
    public get postcodes(): AbstractControl { return this.getFormControl("postcodes"); }
    public get age(): AbstractControl { return this.getFormControl("age"); }
    public get frequency(): AbstractControl { return this.getFormControl("frequency"); }
    public get dietaryNeeds(): AbstractControl { return this.getFormControl("dietaryNeeds"); }
    public get accessibility(): AbstractControl { return this.getFormControl("accessibility"); }
    public get provisionTypes(): AbstractControl { return this.getFormControl("provisionTypes"); }
    public get schoolNeeds(): AbstractControl { return this.getFormControl("schoolNeeds"); }
    public get specialDietCater(): AbstractControl { return this.getFormControl("specialDietCater"); }

    public get postcode(): AbstractControl { return this.getFormControl("postcode"); }
    public get distance(): AbstractControl { return this.getFormControl("distance"); }

    protected constructor(
        formInstance: FormGroup,
        initialSearchParams: SearchParametersModel,
        private emptySearchParams: SearchParametersModel
    ) {
        super(formInstance, initialSearchParams);
    }

    protected bindOnChangeSubscription(): Observable<SearchParametersModel> {
        return merge(
            this.searchText.valueChanges.pipe(map((searchText: string) => ({
                searchText: searchText,
                pageNumber: 1
            }))),
            this.sortOption.valueChanges.pipe(map((sortOption: string) => ({
                searchText: this.initialFormState.searchText,
                sortOption: sortOption,
                pageNumber: 1
            }))),
            this.pageNumber.valueChanges.pipe(map((pageNumber: number) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: pageNumber
            }))),
            this.pageSize.valueChanges.pipe(map((pageSize: number) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                pageSize: pageSize
            }))),
            this.categories.valueChanges.pipe(map((categories: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                categories: categories
            }))),
            this.startDate.valueChanges.pipe(map((startDate: string) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                startDate: startDate
            }))),
            this.endDate.valueChanges.pipe(map((endDate: string) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                endDate: endDate
            }))),
            this.eventCategories.valueChanges.pipe(map((eventCategories: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                eventCategories: eventCategories
            }))),
            this.needsOfChildren.valueChanges.pipe(map((needsOfChildren: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                needsOfChildren: needsOfChildren
            }))),
            this.postcodes.valueChanges.pipe(map((postcodes: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                postcodes: postcodes
            }))),
            this.age.valueChanges.pipe(map((age: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                age: age
            }))),
            this.frequency.valueChanges.pipe(map((frequency: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                frequency: frequency
            }))),
            this.dietaryNeeds.valueChanges.pipe(map((dietaryNeeds: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                dietaryNeeds: dietaryNeeds
            }))),
            this.accessibility.valueChanges.pipe(map((accessibility: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                accessibility: accessibility
            }))),
            this.provisionTypes.valueChanges.pipe(map((provisionTypes: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                provisionTypes: provisionTypes
            }))),
            this.schoolNeeds.valueChanges.pipe(map((schoolNeeds: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                schoolNeeds: schoolNeeds
            }))),
            this.specialDietCater.valueChanges.pipe(map((specialDietCater: Array<string>) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                specialDietCater: specialDietCater
            }))),
            this.postcode.valueChanges.pipe(map((postcode: string) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                postcode: postcode
            }))),
            this.distance.valueChanges.pipe(map((distance: string) => ({
                searchText: this.initialFormState.searchText,
                pageNumber: 1,
                distance: distance
            }))),
        ).pipe(
            map(partialModel => this.getFormModel(partialModel))
        );
    }

    protected getFormModel(partialFormModel: any = {}): SearchParametersModel {
        return _merge(new SearchParametersModel(), super.getFormModel(partialFormModel));
    }

    public reset(): void {
        this.formInstance.patchValue(this.emptySearchParams, { emitEvent: false });
    }
}
